import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../../utils/api';
import { useToast, apiError } from '../../components/Toast';
import { EmptyState } from '../../components/layout/UI';
import { format } from 'date-fns';
import { MailCheck, X } from 'lucide-react';

/**
 * frontend/src/pages/admin/AdminEmailLogPage.jsx
 *
 * The dashboard counted failed emails and linked to System Settings, which
 * does not show them. This is where that card should have gone.
 *
 * It is also what makes "the password reset never arrived" answerable: the
 * attempt is recorded either way, with the reason it failed.
 */

const SAPPHIRE = '#2E539F';
const DANGER   = '#ef4444';

const STATUS = {
  sent:    { fg: '#2f8a84', bg: 'rgba(92,195,188,.16)' },
  failed:  { fg: DANGER,    bg: 'rgba(239,68,68,.10)' },
  pending: { fg: '#8A6400', bg: 'rgba(255,200,73,.20)' },
};

export default function AdminEmailLogPage() {
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') || '';

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getEmailLog({ status: status || undefined });
      setRows(r.data || []);
    } catch (e) {
      toast.error(apiError(e, 'Could not load the email log.'));
    } finally { setLoading(false); }
  }, [status, toast]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set('status', v); else n.delete('status');
    setSp(n);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">Email Log</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
          Every send attempt and what happened to it. A message that never
          arrived is recorded here with the reason.
        </p>
      </div>

      <div className="flex gap-2">
        {[['', 'All'], ['failed', 'Failed'], ['sent', 'Sent'], ['pending', 'Pending']].map(([v, l]) => (
          <button key={v || 'all'} onClick={() => setStatus(v)}
            className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${status === v ? 'font-semibold' : ''}`}
            style={{ borderColor: status === v ? SAPPHIRE : 'var(--border)',
                     background:  status === v ? 'rgba(46,83,159,.06)' : 'var(--surface)',
                     color:       status === v ? SAPPHIRE : 'var(--text-secondary)' }}>
            {l}
            {status === v && <span className="ml-2 font-bold">{rows.length}</span>}
          </button>
        ))}
        {status && (
          <button onClick={() => setStatus('')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm"
            style={{ color: 'var(--text-secondary)' }}>
            Clear <X size={13} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={MailCheck}
          title={status === 'failed' ? 'No failures' : 'Nothing recorded'}
          message={status === 'failed'
            ? 'Every message the system has tried to send has gone out.'
            : 'No email has been attempted yet. If you expected one, check that SMTP is configured.'} />
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--surface-alt)' }}>
                {['When', 'To', 'Subject', 'Type', 'Status', 'Reason'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {rows.map(r => {
                const s = STATUS[r.status] || STATUS.pending;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                      {r.created_at ? format(new Date(r.created_at), 'd MMM HH:mm') : '—'}
                    </td>
                    <td className="px-4 py-2.5 max-w-xs truncate" style={{ color: 'var(--text)' }}
                      title={r.to_email || ''}>{r.to_email}</td>
                    <td className="px-4 py-2.5 max-w-xs truncate" style={{ color: 'var(--text-secondary)' }}
                      title={r.subject || ''}>{r.subject}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs"
                      style={{ color: 'var(--text-secondary)' }}>{r.event_type}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: s.bg, color: s.fg }}>{r.status}</span>
                    </td>
                    <td className="px-4 py-2.5 max-w-md" style={{ color: DANGER }}
                      title={r.error_message || ''}>
                      <span className="text-xs">{r.error_message || ''}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        The 200 most recent. A failure here is almost always configuration
        rather than the account it was sent to — the reason column says which.
      </p>
    </div>
  );
}
